// @martyrs/src/modules/auth/locales/en.js
export default {
  auth: {
    // Title of the sign-in / sign-up section (route.meta.title of its routes).
    title: 'Authentication',
    invite: {
      title: 'Invite',
    },
    users: {
      title: 'Users',
    },
    profile: {
      title: 'Profile',
    },
    dashboard: {
      title: 'Dashboard',
    },
    settings: {
      title: 'Settings',
    },
    // Verification code email (otp.service.js). The server prints it through
    // translate in the request language into the design-system email layout
    // (render-email.js); {name} is the brand. The code itself goes into the
    // layout's code block, not into the text.
    email: {
      otp: {
        subject: '{name} verification code',
        title: 'Your verification code',
        text: 'Enter this code on {name} to continue. The code works for 10 minutes.',
        footnote: 'If you did not request the code, ignore this email.',
      },
    },
    // Verification code by SMS — the same code over another channel.
    sms: {
      otp: {
        body: '{name} verification code: {code}',
      },
    },
    // User profile navigation (views/configs/navigation.user.config.js).
    nav: {
      sections: {
        profile: 'Profile',
        orders: 'Orders',
      },
      dashboard: 'Dashboard',
      notifications: 'Notifications',
      settings: 'Settings',
      profile: 'Profile',
      account: 'Account',
      events: 'Events',
      groups: 'Groups',
      posts: 'Posts',
      orders: 'Orders',
    },
    // Profile settings (ProfileEditProfile / ProfileEditAccount / sidebar).
    profileEdit: {
      title: 'Profile Settings',
      profile: {
        title: 'Profile',
        uploadUserpic: 'Upload userpic',
        usernameHint: 'Your username becomes the profile URL: /users/<name>',
        name: 'Name',
        description: 'Description',
        notSpecified: 'Not specified',
        socialsTitle: 'Socials',
        socialsHint: 'Provide only the username, without the full link.',
        save: 'Save',
      },
      account: {
        contactsTitle: 'Phone and Email',
        emailTitle: 'Email',
        phone: 'Phone',
        phonePlaceholder: 'Enter phone number',
        changePhone: 'Change Phone',
        savePhone: 'Save Phone',
        email: 'Email',
        emailPlaceholder: 'Enter email',
        changeEmail: 'Change Email',
        saveEmail: 'Save Email',
        cancel: 'Cancel',
        languageTitle: 'Language',
        language: 'Interface language',
        countryTitle: 'Country',
        country: 'Country',
        countryHint: 'Can be changed once every 30 days',
        settingsTitle: 'Settings',
        darkmode: 'Dark mode',
        accountTitle: 'Account',
        logout: 'Log out',
        deleteAccount: 'Delete Account',
        deleteConfirm: 'Delete your account? Restoration is possible only with the help of an administrator.',
      },
    },
    popup: {
      title: "Welcome",
      subtitle: "Please sign in or create an account to continue.",
      signUp: "Sign Up",
      haveAccount: "Already have an account?",
      signIn: "Sign in"
    },
    signIn: {
      // title is the page name (browser tab, breadcrumbs), greeting is the
      // welcome line on the screen itself: different texts, not two names.
      title: "Sign In",
      // Без имени бренда, как в ru: строку видит любой проект на martyrs.
      greeting: "Welcome,",
      subtitle: "Sign In to Continue",
      signup: "Don't have an account?",
      phone: "Phone",
      email: "Email",
      phonePlaceholder: "Enter your phone",
      emailPlaceholder: "Enter your email",
      haveAccount: "Don't have an account? Sign Up",
      passwordPlaceholder: "Enter your password",
      forgotPassword: "Forgot Password?",
      loginByCode: "Sign in with a code",
      signin: "Sign In",
      signin_apple: "Sign In with Apple",
      soon: "SOON"
    },
    signUp: {
      title: "Sign Up",
      description: "Choose a convenient way to register via phone or email:",
      phone: "Phone",
      email: "Email",
      phonePlaceholder: "Enter your phone",
      emailPlaceholder: "Enter your email",
      emailNotice: "We will send you an email with a confirmation code that you will need to enter in the next step.",
      smsNotice: "We will send you an SMS with a confirmation code that you will need to enter in the next step.",
      sendCode: "Send Code",
      codePlaceholder: "Code from the email",
      back: "Back",
      home: "Go to Home",
      haveAccount: "Already have an account?",
      agreement: "By registering, you agree to our <a href='/legal/eula' target='_blank'>End User License Agreement (EULA)</a>"
    },
    // Messages of the form checks on sign-in, sign-up, password reset and invite.
    validation: {
      phone: 'Incorrect phone number',
      email: 'Incorrect email',
      password: 'Incorrect password',
      code: 'Enter the code',
      codeFromEmail: 'Enter the code from the email',
      // Field checks (views/validations/inputs.validation.js).
      length: 'Fill in the field.',
      emailFormat: 'Invalid email format',
      passwordLength: 'The password must be at least 8 characters',
    },
    enterCode: {
      verifyNumberTitle: "Verify Number",
      verifyEmailTitle: "Verify Email",
      instructions: "We have sent a registration code to your phone number. Please enter it in the field below:",
      instructionsEmail: "We have sent a registration code to your email. Please enter it in the field below:",
      verifyChangeTitle: "Verify Change",
      current: "Current",
      new: "New",
      confirm: "Confirm",
      resendCode: "Send the code again",
      secondsResend: "seconds to the possibility of sending the code again.",
      resendIn: "You can resend the code in {n} s"
    },
    resetPassword: {
      title: "Reset Password",
      forgotPasswordTitle: "Forgot Password?",
      instructions: "To reset your password, specify the phone or email used during registration.",
      smsInfo: "We will send you an SMS with a confirmation code to reset your password, which you will need to enter in the next step.",
      emailInfo: "We will send you an email with a confirmation code to reset your password, which you will need to enter in the next step.",
      phone: "Phone",
      email: "Email",
      phonePlaceholder: "Enter your phone",
      emailPlaceholder: "Enter your email",
      sendCode: "Send code"
    },
    enterPassword: {
      almostDone: "Almost there!",
      newPasswordPrompt: "Please, set a new password.",
      registrationPasswordPrompt: "Please, set your password to complete the registration.",
      passwordPlaceholder: "Password",
      repeatPasswordPlaceholder: "Repeat Password",
      changePasswordBtn: "Change Password",
      registerBtn: "Register"
    },
    features: [
      {
        title: "Everything in one place",
        description: "Manage your account, data and settings from a single interface."
      },
      {
        title: "Secure by default",
        description: "Code-based sign-in and safe data storage keep your account protected."
      },
      {
        title: "Quick start",
        description: "Sign up in a minute and get to work right away."
      },
      {
        title: "Support at hand",
        description: "Need help? We're here and ready to answer your questions."
      }
    ]
  }
};